import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'language_settings';

export interface LanguageLabels {
  lbl_login_txt: string;
  lbl_email: string;
  lbl_login_success_msg: string;
  lbl_open_invoice: string;
  lbl_past_invoice: string;
  lbl_concern: string;
  lbl_logout_txt: string;
  lbl_logout_confirmation_msg: string;
  lbl_company_name: string;
  lbl_services_availed: string;
  lbl_company_address: string;
  lbl_coordinator: string;
  lbl_contact_no: string;
  lbl_marketer_contact_info: string;
  lbl_on_call_need_to_know: string;
  lbl_message: string;
  lbl_forgot_password: string;
  lbl_forgot_password_popup_message: string;
  lbl_username: string;
}

const defaultLabels: LanguageLabels = {
  lbl_login_txt: 'Log in',
  lbl_email: 'Email Address',
  lbl_login_success_msg: 'Logged in successfully',
  lbl_open_invoice: 'Open Invoice',
  lbl_past_invoice: 'Past Invoice',
  lbl_concern: 'Concern',
  lbl_logout_txt: 'Log out',
  lbl_logout_confirmation_msg: 'Are you sure you want to log out?',
  lbl_company_name: 'Company Name',
  lbl_services_availed: 'Daytime Service',
  lbl_company_address: 'Company Address',
  lbl_coordinator: 'Coordinator',
  lbl_contact_no: 'Contact Number',
  lbl_marketer_contact_info: 'Marketer/Intake Contact Information',
  lbl_on_call_need_to_know: 'On-Call Info',
  lbl_message: 'Message',
  lbl_forgot_password: 'Forgot Password',
  lbl_forgot_password_popup_message:
    'Please reach out to info@iheart-hc.com to get your password reset.',
  lbl_username: 'Username',
};

interface LanguageContextValue {
  labels: LanguageLabels;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function loadStoredLabels(): LanguageLabels | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LanguageLabels>;
    if (!parsed || typeof parsed !== 'object') return null;
    return { ...defaultLabels, ...parsed };
  } catch {
    return null;
  }
}

function saveLabelsToStorage(labels: LanguageLabels): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(labels));
  } catch (e) {
    console.error('Failed to save language settings', e);
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [labels, setLabels] = useState<LanguageLabels>(() => loadStoredLabels() ?? defaultLabels);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLabels = useCallback(async () => {
    const baseUrl =
      process.env.REACT_APP_LIVE_API_URL || process.env.REACT_APP_API_BASE_URL || '';
    const url = `${baseUrl}language-setting/list`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();

      if (response.ok && result?.status === true && result?.data && typeof result.data === 'object') {
        const next: LanguageLabels = { ...defaultLabels, ...result.data };
        setLabels(next);
        saveLabelsToStorage(next);
      } else {
        const stored = loadStoredLabels();
        if (stored) setLabels(stored);
      }
    } catch {
      const stored = loadStoredLabels();
      if (stored) setLabels(stored);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const value: LanguageContextValue = {
    labels,
    isLoading,
    refetch: fetchLabels,
  };

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLabels(): LanguageLabels {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLabels must be used within LanguageProvider');
  return ctx.labels;
}
